import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Composant générique pour afficher des suggestions dans un portail React
 * Ce composant permet d'afficher des suggestions en dehors des limites d'un modal ou d'un conteneur avec overflow
 * 
 * @param {Object} props - Les propriétés du composant
 * @param {boolean} props.show - Si le portail doit être affiché ou non
 * @param {React.RefObject} props.anchorRef - Référence à l'élément d'ancrage (input)
 * @param {React.ReactNode} props.children - Le contenu à afficher dans le portail
 * @param {number} props.zIndex - L'index z du portail (défaut: 9999)
 * @param {Object} props.style - Styles supplémentaires à appliquer au portail
 */
const SuggestionsPortal = ({ 
  show, 
  anchorRef, 
  children, 
  zIndex = 9999,
  style = {}
}) => {
  const [position, setPosition] = useState(null)

  // Mettre à jour la position quand le portail est affiché ou quand la fenêtre est redimensionnée
  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef?.current && show) {
        const rect = anchorRef.current.getBoundingClientRect()
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        })
      }
    }

    updatePosition()
    
    // Ajouter un écouteur pour le redimensionnement de la fenêtre
    window.addEventListener('resize', updatePosition)
    // Nettoyer l'écouteur lors du démontage du composant
    return () => window.removeEventListener('resize', updatePosition)
  }, [show, anchorRef])

  if (!show || !position) return null

  const portalStyle = {
    position: 'fixed',
    top: `${position.top}px`,
    left: `${position.left}px`,
    width: `${position.width}px`,
    zIndex,
    ...style
  }

  return createPortal(
    <div style={portalStyle}>
      {children}
    </div>,
    document.body
  )
}

export default SuggestionsPortal